import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Shared geometries - created once, reused
const sharedStreakGeometry = new THREE.PlaneGeometry(0.08, 0.5);

// Temp objects for matrix calculations
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempScale = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();

const HardDropTrail = ({ startY, endY, columns, onComplete }) => {
  const streakInstanceRef = useRef();
  const trailInstanceRef = useRef();
  const startTime = useRef(null);
  const trailHeight = Math.abs(startY - endY);

  // Generate wind streak data
  const windStreakData = useMemo(() => {
    const streaks = [];
    columns.forEach((col) => {
      const x = col - 5 + 0.5;
      const y = endY + 1;
      // 2 streaks per column - left and right
      streaks.push({ x: x - 0.3, y, side: -1, delay: 0 });
      streaks.push({ x: x + 0.3, y, side: 1, delay: 0 });
    });
    return streaks;
  }, [columns, endY]);

  // Generate glow trail data
  const glowTrailData = useMemo(() => {
    return columns.map((col) => ({
      x: col - 5 + 0.5,
      startY,
      endY,
      height: trailHeight,
    }));
  }, [columns, startY, endY, trailHeight]);

  const streakCount = windStreakData.length;

  // Streak material
  const streakMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.25,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  // Create dynamic trail geometry (height varies)
  const trailGeometries = useMemo(() => {
    return glowTrailData.map(data => new THREE.PlaneGeometry(0.5, data.height));
  }, [glowTrailData]);

  // Initialize instances
  useEffect(() => {
    if (streakInstanceRef.current) {
      windStreakData.forEach((data, i) => {
        tempPosition.set(data.x, data.y, 0.1);
        tempScale.set(1, 1, 1);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        streakInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      streakInstanceRef.current.instanceMatrix.needsUpdate = true;
      streakInstanceRef.current.visible = false;
    }
  }, [windStreakData]);

  // Single consolidated useFrame for all animations
  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;

    if (elapsed > 0.25) {
      onComplete?.();
      return;
    }

    // Animate glow trails
    if (trailInstanceRef.current && trailInstanceRef.current.children) {
      trailInstanceRef.current.children.forEach((trail, i) => {
        if (!trail) return;
        const data = glowTrailData[i];

        if (elapsed > 0.15) {
          trail.visible = false;
          return;
        }

        trail.visible = true;
        const progress = elapsed / 0.15;
        const shrink = 1 - progress;
        trail.scale.y = shrink;
        trail.position.y = data.endY + (data.height * shrink) / 2;
        trail.material.opacity = 0.15 * (1 - progress);
      });
    }

    // Animate wind streaks using instanced mesh
    if (streakInstanceRef.current) {
      let anyVisible = false;
      windStreakData.forEach((data, i) => {
        const adjustedTime = elapsed - data.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.2) {
          tempScale.setScalar(0);
          tempPosition.set(data.x, data.y, 0.1);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          streakInstanceRef.current.setMatrixAt(i, tempMatrix);
        } else {
          anyVisible = true;
          const progress = adjustedTime / 0.2;
          const easeOut = 1 - Math.pow(1 - progress, 2);

          const x = data.x + data.side * easeOut * 0.4;
          const y = data.y + easeOut * 0.5;

          tempPosition.set(x, y, 0.1);
          tempScale.set(1, 0.5 * (1 - progress * 0.5), 1);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          streakInstanceRef.current.setMatrixAt(i, tempMatrix);
        }
      });
      streakInstanceRef.current.visible = anyVisible;
      streakInstanceRef.current.instanceMatrix.needsUpdate = true;
      streakInstanceRef.current.material.opacity = 0.25 * (1 - elapsed / 0.2);
    }
  });

  return (
    <group>
      {/* Glow trails - individual meshes due to varying heights */}
      <group ref={trailInstanceRef}>
        {glowTrailData.map((data, i) => (
          <mesh
            key={`glow-${i}`}
            position={[data.x, data.endY + data.height / 2, 0.02]}
            geometry={trailGeometries[i]}
            visible={false}
          >
            <meshBasicMaterial
              color={0x88aacc}
              transparent
              opacity={0.15}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        ))}
      </group>

      {/* Wind streaks - Instanced for performance */}
      {streakCount > 0 && (
        <instancedMesh
          ref={streakInstanceRef}
          args={[sharedStreakGeometry, streakMaterial, streakCount]}
          frustumCulled={false}
          visible={false}
        />
      )}
    </group>
  );
};

export default HardDropTrail;
